import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Inbox, AlertTriangle, MessageCircle, Mail, ChevronRight, Building2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTS = [
  { value: "open", label: "未対応", tone: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "in_progress", label: "対応中", tone: "bg-blue-100 text-blue-800 border-blue-200" },
  { value: "resolved", label: "解決済", tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
];
const statusOf = (v) => STATUS_OPTS.find((s) => s.value === v) || STATUS_OPTS[0];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function InquiriesManagement() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inquiries");
      setInquiries(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return inquiries;
    return inquiries.filter((i) => i.status === statusFilter);
  }, [inquiries, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: inquiries.length };
    for (const s of STATUS_OPTS) c[s.value] = inquiries.filter((i) => i.status === s.value).length;
    return c;
  }, [inquiries]);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/inquiries/${id}`, { status });
      setInquiries((prev) => prev.map((x) => x.id === id ? { ...x, status } : x));
      toast.success("ステータスを更新しました");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="inquiries-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">お問い合わせ</h1>
        <p className="text-sm text-slate-500 mt-1">アプリ内お問い合わせフォームからの受付一覧です。</p>
      </div>

      <Card className="p-4 bg-white border border-slate-200">
        <div className="flex flex-wrap items-center gap-2">
          <FilterPill label={`全て (${counts.all})`} active={statusFilter === "all"} onClick={() => setStatusFilter("all")} testId="filter-all" />
          {STATUS_OPTS.map((s) => (
            <FilterPill key={s.value} label={`${s.label} (${counts[s.value] || 0})`} active={statusFilter === s.value} onClick={() => setStatusFilter(s.value)} testId={`filter-${s.value}`} />
          ))}
        </div>
      </Card>

      <Card className="bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <p className="py-12 text-center text-sm text-slate-400">読み込み中…</p>
        ) : filtered.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-400">該当するお問い合わせはありません</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((i) => (
              <InquiryRow key={i.id} inquiry={i} onShowDetail={() => setDetail(i)} onStatus={(s) => setStatus(i.id, s)} />
            ))}
          </ul>
        )}
      </Card>

      <InquiryDetailDialog inquiry={detail} onClose={() => setDetail(null)} onStatus={(s) => { setStatus(detail.id, s); setDetail({ ...detail, status: s }); }} />
    </div>
  );
}

function FilterPill({ label, active, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
        active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
      }`}>
      {label}
    </button>
  );
}

function InquiryRow({ inquiry, onShowDetail, onStatus }) {
  const s = statusOf(inquiry.status);
  return (
    <li className="px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer" onClick={onShowDetail} data-testid={`inquiry-row-${inquiry.id}`}>
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 w-9 h-9 shrink-0 rounded-full flex items-center justify-center ${
          inquiry.priority === "urgent" ? "bg-rose-100 text-rose-600" : "bg-blue-100 text-blue-600"
        }`}>
          {inquiry.priority === "urgent" ? <AlertTriangle className="w-4 h-4" /> : <MessageCircle className="w-4 h-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[11px] px-2 py-0.5 rounded-full border font-bold ${s.tone}`}>{s.label}</span>
            <span className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{inquiry.category_label}</span>
            {inquiry.priority === "urgent" && (
              <span className="text-[10px] bg-rose-600 text-white font-bold px-2 py-0.5 rounded">🔥 すぐ困ってる</span>
            )}
            <span className="text-[11px] text-slate-400 ml-auto">{fmtDate(inquiry.created_at)}</span>
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-700">
            <Building2 className="w-3 h-3 text-slate-400" />
            <span className="font-bold">{inquiry.company || "—"}</span>
            <span className="text-slate-500">/ {inquiry.user_name}</span>
            <span className="text-slate-400">({inquiry.user_role === "manager" ? "店長" : inquiry.user_role === "staff" ? "スタッフ" : inquiry.user_role})</span>
          </div>
          <p className="mt-1 text-xs text-slate-600 line-clamp-2 whitespace-pre-wrap">{inquiry.body}</p>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 mt-2" />
      </div>
    </li>
  );
}

function InquiryDetailDialog({ inquiry, onClose, onStatus }) {
  if (!inquiry) return null;
  const s = statusOf(inquiry.status);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inquiry-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-blue-600" />お問い合わせ詳細
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[11px] px-2 py-0.5 rounded-full border font-bold ${s.tone}`}>{s.label}</span>
            <span className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{inquiry.category_label}</span>
            {inquiry.priority === "urgent" && <span className="text-[10px] bg-rose-600 text-white font-bold px-2 py-0.5 rounded">🔥 すぐ困ってる</span>}
            <span className="text-[11px] text-slate-400 ml-auto">{fmtDate(inquiry.created_at)}</span>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-1">
            <div><span className="text-slate-500 text-xs">会社/店舗:</span> <b>{inquiry.company || "—"}</b></div>
            <div><span className="text-slate-500 text-xs">送信者:</span> {inquiry.user_name}（{inquiry.user_role === "manager" ? "店長" : inquiry.user_role === "staff" ? "スタッフ" : inquiry.user_role}・@{inquiry.user_login_id || "—"}）</div>
            <div className="flex items-center gap-1">
              <Mail className="w-3 h-3 text-slate-400" />
              <span className="text-slate-500 text-xs">返信先:</span>
              {inquiry.user_email ? (
                <a href={`mailto:${inquiry.user_email}`} className="text-blue-600 hover:underline text-sm">{inquiry.user_email}</a>
              ) : <span className="text-slate-400 text-xs">なし</span>}
            </div>
          </div>

          <div>
            <p className="text-xs font-bold text-slate-700 mb-1.5">内容</p>
            <div className="border border-slate-200 rounded-lg p-3 text-sm whitespace-pre-wrap bg-white" data-testid="inquiry-body-text">{inquiry.body}</div>
          </div>

          <div>
            <p className="text-xs font-bold text-slate-700 mb-1.5">ステータス操作</p>
            <div className="flex gap-2 flex-wrap">
              {STATUS_OPTS.map((opt) => (
                <Button
                  key={opt.value}
                  variant={inquiry.status === opt.value ? "default" : "outline"}
                  size="sm"
                  className={inquiry.status === opt.value ? "bg-blue-600 hover:bg-blue-700" : ""}
                  onClick={() => onStatus(opt.value)}
                  data-testid={`set-status-${opt.value}`}
                >
                  {opt.label}に
                </Button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          {inquiry.user_email && (
            <Button
              variant="outline"
              onClick={() => {
                const subject = encodeURIComponent(`Re: ${inquiry.category_label} — ${inquiry.company || inquiry.user_name}`);
                const body = encodeURIComponent(`${inquiry.user_name} 様\n\n\n\n---\n元のお問い合わせ:\n${inquiry.body}`);
                window.open(`mailto:${inquiry.user_email}?subject=${subject}&body=${body}`, "_blank");
              }}
              data-testid="reply-mail"
            >
              <Mail className="w-4 h-4 mr-1" />メールで返信
            </Button>
          )}
          <Button onClick={onClose} data-testid="inquiry-detail-close">閉じる</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
